import { useEffect, useState } from 'react'
import { collection, limit, orderBy, query, serverTimestamp } from 'firebase/firestore'
import { addDoc, onSnapshot } from '@/lib/metered'
import { db } from './firebase'
import { changePayload } from './changeEntry'

/**
 * Append-only change history, kept as a subcollection on the record it describes.
 *
 * The record itself carries only the current value plus who set it last. That answers
 * "what is it now" and nothing else — it cannot tell you that a field was Migrate for a
 * fortnight before someone changed their mind, which is exactly the question that gets
 * asked months later when the migration behaves unexpectedly. On a time-and-materials
 * engagement the answer needs a date and a name.
 *
 * Nothing here is ever updated or deleted; the rules forbid both. A log that can be
 * rewritten is not evidence.
 */

export type ChangeEntry = {
  id: string
  field: string
  from: string
  to: string
  note?: string
  byEmail: string
  byName: string | null
  at: { seconds: number } | null
  /**
   * Decision activity entries written before the change log existed carry `kind` and
   * `body` instead of field/from/to. They are rendered as narrative rather than dropped —
   * they are the only record of what happened in that period.
   */
  kind?: 'answer' | 'comment' | 'status'
  body?: string
  /**
   * What kind of record this entry is about — `decision`, `task`, `staff`, `orgContainer`,
   * `orgNote`, and so on, the same vocabulary `kindCapability()` in `firestore.rules` and
   * `CAPABILITY_FOR` in `features/activity/events.ts` already use. Stamped by `recordChange`
   * below from the collection it is writing into, not supplied by callers — every write
   * site already names its own collection, and deriving the kind from that one spot is the
   * only way it cannot drift from `KIND_OF_COLLECTION`.
   *
   * It exists so a reader can run ONE collection-group query across every `activity`
   * subcollection in the project and still ask only for the kind it has a capability to
   * see — the same reasoning `parentKind` already serves on `comments` and
   * `activityEvents`. An entry written before this field existed has none, which
   * `kindCapability('')` resolves to `no.such.capability` — invisible to a collection-group
   * read, same as an unrecognised kind on a comment, but still readable the old way,
   * through the parent it hangs off.
   */
  parentKind?: string
}

export type Actor = { email: string; name?: string | null }

/**
 * The kind a `recordChange` write is about, keyed by the Firestore collection it writes
 * into. Mirrors `kindCapability()` in `firestore.rules` exactly — these are the same
 * strings, because the rule and this map both have to agree on what a collection-group
 * query for one kind actually contains. `fieldDecisions` reads oddly next to `field`, but
 * that is the collection's real name (the field guide's own review rows), not a typo.
 */
const KIND_OF_COLLECTION: Record<string, string> = {
  decisions: 'decision',
  fieldDecisions: 'field',
  products: 'product',
  owners: 'owner',
  tasks: 'task',
  gates: 'gate',
  staff: 'staff',
  orgContainers: 'orgContainer',
  orgNotes: 'orgNote',
  orgSharingRules: 'orgSharingRule',
}

export async function recordChange(
  path: [collection: string, docId: string],
  change: { field: string; from: string; to: string; note?: string },
  actor: Actor,
): Promise<void> {
  // Never record a no-op: a log full of "Open → Open" is a log nobody reads.
  if (change.from === change.to && !(change.note ?? '').trim()) return
  // Every collection this ever writes into is in `KIND_OF_COLLECTION` — the rules'
  // collection-group read for `activity` (`firestore.rules`) keys entirely on
  // `parentKind`, so a collection missing from this map would not fail loudly: the
  // entry would still write, just invisible to that read, with nothing anywhere saying
  // so. Failing here instead of falling back to `path[0]` means a future collection that
  // forgets to add itself here breaks at the write, in a stack trace, rather than going
  // silently unreadable months later.
  const parentKind = KIND_OF_COLLECTION[path[0]]
  if (!parentKind) {
    throw new Error(
      `recordChange: "${path[0]}" has no parentKind in KIND_OF_COLLECTION (src/lib/activity.ts) — `
      + 'add it there and to kindCapability() in firestore.rules before writing history for it.',
    )
  }
  await addDoc(collection(db, path[0], path[1], 'activity'), {
    // Built rather than spread: Firestore rejects `undefined` and fails the whole write,
    // and an optional field written as `note: text || undefined` still carries the key.
    ...changePayload(change, actor),
    parentKind,
    at: serverTimestamp(),
  })
}

export function useChangeLog(collectionName: string, docId: string | null, max = 50) {
  const [entries, setEntries] = useState<ChangeEntry[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!docId) { setEntries([]); setLoading(false); return }
    setLoading(true)
    return onSnapshot(
      query(
        collection(db, collectionName, docId, 'activity'),
        orderBy('at', 'desc'),
        limit(max),
      ),
      (snap) => {
        setEntries(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }) as ChangeEntry)
            // Comments have moved to their own collection; showing them here as well
            // would duplicate every one of them in the panel.
            .filter((e) => e.kind !== 'comment'),
        )
        setLoading(false)
      },
      () => { setEntries([]); setLoading(false) },
    )
  }, [collectionName, docId, max])

  return { entries, loading }
}
